import React from 'react';
import type {SetRequired} from 'type-fest';

import {defaultRangeExtractor, type Range} from '@tanstack/react-virtual';
import {useVirtualizer} from '@packages/systems/spring-hooks';
import {tokens, useSize} from '@packages/utilities/styled';

import {CREATABLE_OPTION_VALUE, isOptionGroup} from '../../../hooks';

import {
  useSearchableListBox,
  useSearchableListContext,
} from '../../SearchableList';
import {SearchableListItemWrapper} from '../../SearchableList/SearchableListItem';
import {SearchableListItemGroupView} from '../../SearchableList/SearchableListBox';
import type {SearchableListGroupProps} from '../../SearchableList';
import Spinner from '../../Spinner';
import View from '../../View';

import {
  MEDIUM_AUTOCOMPLETE_OPTION_HEIGHT,
  X_LARGE_AUTOCOMPLETE_OPTION_HEIGHT,
} from './_consts';
import type {AutocompleteListBoxProps} from './_types';

import {
  AutocompleteListBoxContentView,
  AutocompleteListBoxView,
} from './AutocompleteListBox.styles';
import {AutocompleteCreatableOption} from '../AutocompleteOption';
import {AutocompleteEmptyState} from './AutocompleteEmptyState';
import {AutocompleteGroupLabel} from './AutocompleteGroupLabel';
import {AutocompleteNote} from './AutocompleteNote';
import {useAutocompleteNoteText} from './useAutocompleteNoteText';

const VirtualizedAutocompleteListBoxGroup = ({
  density,
  label,
  getEstimateSize,
  hasGroupDividers,
  isLastChild,
  isOverscan,
  measureElement,
  options,
  renderOption,
  scrollContainerRef,
  scrollMargin,
  shouldHideLabel,
  verticalItemAlignment,
  virtualIndex,
}: SearchableListGroupProps & {
  getEstimateSize: () => number;
  hasGroupDividers: boolean;
  isLastChild: boolean;
  isOverscan: boolean;
  measureElement: ReturnType<typeof useVirtualizer>['measureElement'];
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  scrollMargin: number;
  virtualIndex: number;
}) => {
  const groupLabelId = React.useId();

  const [paddingStart, setPaddingStart] = React.useState(0);

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: getEstimateSize,
    scrollMargin,
    paddingStart,
    paddingEnd: hasGroupDividers && !isLastChild ? 8 : 0,
    overscan: isOverscan ? 0 : 25, // limit overscan on groups that are themselves overscanned.
    indexAttribute: 'data-virtual-index',
  });

  const labelCallbackRef: React.RefCallback<HTMLElement> = React.useCallback(
    (node: HTMLElement) => {
      setPaddingStart(node?.offsetHeight ?? 0);
    },
    []
  );

  const virtualOptions = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const groupStyle = React.useMemo(() => {
    return {
      height: totalSize,
      position: 'relative',
    } as const;
  }, [totalSize]);

  return (
    <SearchableListItemGroupView
      aria-labelledby={groupLabelId}
      data-virtual-index={virtualIndex}
      ref={measureElement}
      role="group"
      style={groupStyle}
    >
      <AutocompleteGroupLabel
        id={groupLabelId}
        isVisuallyHidden={shouldHideLabel}
        ref={labelCallbackRef}
      >
        {label}
      </AutocompleteGroupLabel>
      {virtualOptions.map((row) => {
        const option = options[row.index];
        if (!option) return null;

        return (
          <div
            key={row.index}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${row.start - scrollMargin}px)`,
            }}
          >
            <SearchableListItemWrapper
              categoryLabel={label}
              density={density}
              measureElement={virtualizer.measureElement}
              option={option}
              renderOption={renderOption}
              verticalItemAlignment={verticalItemAlignment}
              virtualIndex={row.index}
            />
          </div>
        );
      })}
    </SearchableListItemGroupView>
  );
};

/**
 * This is largely the same as the VirtualizedSearchableListBox, but with changes to its render return.
 *
 * We might want to refactor a bit of this into a shared hook in the future.
 */
export const VirtualizedAutocompleteListBox = React.forwardRef<
  HTMLDivElement,
  SetRequired<Omit<AutocompleteListBoxProps, 'isVirtualized'>, 'maxHeight'> & {
    /**
     * The reason we're needing to pass the scrollContainerRef is because Floating UI needs to be attached to the entire container,
     * but the scrollable area that the virtualizer is concerned with is rendering within that.
     *
     * This is a bit different than how it's handled in the VirtualizedSearchableListBox, but it's necessary for this component.
     */
    scrollContainerRef: React.RefObject<HTMLDivElement>;
  }
>(
  (
    {
      density = 'compact',
      hasGroupDividers = false,
      isCreatable,
      maxHeight,
      note: noteProp,
      renderEmptyStateLabel,
      style: styleProp,
      scrollContainerRef,
      verticalItemAlignment = 'top',
      ...props
    },
    ref
  ) => {
    const size = useSize();
    const {floatingStyles, isLoading} = useSearchableListContext();

    const {
      mergedProps,
      options: optionsProp,
      renderOption,
    } = useSearchableListBox({
      ref,
      ...props,
    });

    const autocompleteNote = useAutocompleteNoteText({defaultNote: noteProp});
    const noteRef = React.useRef<HTMLDivElement>(null);
    const [_noteHeight, setNoteHeight] = React.useState<number>(0);

    const creatablePortalRef = React.useRef<HTMLDivElement>(null);
    const [_isRenderingCreatable, setIsRenderingCreatable] =
      React.useState<boolean>(false);

    const visibleRangeRef = React.useRef<[number, number]>([0, 0]);

    /**
     * Grab the indices of the grouped options, so we can always render their wrappers.
     * This lets use preserve their item height within the virtualizer.
     */
    const groupIndices = React.useMemo(() => {
      const acc: number[] = [];

      optionsProp.forEach((option, i) => {
        if (isOptionGroup(option)) {
          acc.push(i);
        }
      });

      return acc;
    }, [optionsProp]);

    const hasCreatableOption =
      isCreatable &&
      optionsProp[optionsProp.length - 1]?.value === CREATABLE_OPTION_VALUE;

    const creatableIndex = React.useMemo(() => {
      if (hasCreatableOption) {
        return optionsProp.length - 1;
      }
      return null;
    }, [hasCreatableOption, optionsProp]);

    const rangeExtractor = React.useCallback(
      (range: Range) => {
        /**
         * We need to keep our grouped options in the range, so we can render their wrappers.
         * These wrappers are used to preserve space and prevent scroll jumping and weird layout issues.
         */
        const next = new Set([
          ...groupIndices,
          ...defaultRangeExtractor(range),
        ]);
        visibleRangeRef.current = [range.startIndex, range.endIndex];

        if (creatableIndex !== null) {
          next.add(creatableIndex);
        }

        return [...next].sort((indexA, indexB) => indexA - indexB);
      },
      [groupIndices, creatableIndex]
    );

    const getEstimateSize = React.useCallback(() => {
      if (size === 'x-large') {
        return X_LARGE_AUTOCOMPLETE_OPTION_HEIGHT;
      } else {
        return MEDIUM_AUTOCOMPLETE_OPTION_HEIGHT;
      }
    }, [size]);

    const count = optionsProp.length;

    const virtualizer = useVirtualizer({
      count,
      getScrollElement: () => scrollContainerRef.current,
      estimateSize: getEstimateSize,
      rangeExtractor,
      overscan: 25,
      indexAttribute: 'data-virtual-index',
    });

    const virtualOptions = virtualizer.getVirtualItems();

    const style = React.useMemo(() => {
      return {
        ...styleProp,
        ...floatingStyles,
      };
    }, [floatingStyles, styleProp]);

    const totalSize = virtualizer.getTotalSize();

    const scrollContainerStyles = React.useMemo(() => {
      /**
       * Our creatable option is portaled out, so we need to subtract its height from the total virtualized scroll area if it is present.
       */
      if (hasCreatableOption) {
        if (optionsProp.length === 1) {
          /**
           * If the creatable option is the only option in the array, we essentially need the height of the scroll container to be 0px.
           * We can't actually make it 0px, because the virtualizer needs at least 1px of height to work.
           * So, we set the height to 1px.
           */
          return {
            height: 1,
          };
        } else if (optionsProp.length > 1) {
          return {
            height: totalSize - getEstimateSize(),
          };
        }
      }

      return {
        height: totalSize,
      };
    }, [totalSize, getEstimateSize, hasCreatableOption, optionsProp]);

    return (
      <AutocompleteListBoxView
        hasGroupDividers={hasGroupDividers}
        isVirtualized
        {...mergedProps}
        style={style}
      >
        <AutocompleteListBoxContentView
          data-virtual-scroll-container
          ref={scrollContainerRef}
          role="presentation"
          style={{maxHeight}}
        >
          {isLoading ? (
            <View
              aria-label="Loading..."
              display="flex"
              justifyContent="center"
              padding="$5 $0"
              role="option"
            >
              <Spinner />
            </View>
          ) : virtualOptions.length ? (
            <>
              {/**
               * This div lets the scroll bar represent the true height of the scrollable area.
               *
               * If we don't have this, the element scroll bar appears to be at the end, even though there is still more to show.
               */}
              <div style={scrollContainerStyles}>
                {virtualOptions.map((option, index) => {
                  const row = optionsProp[option.index];

                  if (!row) {
                    return null;
                  }

                  if (isOptionGroup(row)) {
                    const {
                      options: groupedOptions,
                      label,
                      shouldHideGroupLabel,
                    } = row;

                    if (!groupedOptions.length) {
                      return null;
                    }

                    const isLastChild = hasCreatableOption
                      ? index === virtualOptions.length - 2
                      : index === virtualOptions.length - 1;
                    return (
                      <VirtualizedAutocompleteListBoxGroup
                        density={density}
                        getEstimateSize={getEstimateSize}
                        hasGroupDividers={hasGroupDividers}
                        isLastChild={isLastChild}
                        isOverscan={
                          option.index < visibleRangeRef?.current[0] - 1 ||
                          option.index > visibleRangeRef?.current[1] + 1
                        }
                        key={`${label}-${option.index}`}
                        label={label}
                        measureElement={virtualizer.measureElement}
                        options={groupedOptions}
                        renderOption={renderOption}
                        scrollContainerRef={scrollContainerRef}
                        scrollMargin={option.start}
                        shouldHideLabel={shouldHideGroupLabel}
                        verticalItemAlignment={verticalItemAlignment}
                        virtualIndex={option.index}
                      />
                    );
                  }

                  if (row.value === CREATABLE_OPTION_VALUE) {
                    return (
                      <AutocompleteCreatableOption
                        density={density}
                        key={option.index}
                        onMount={() => setIsRenderingCreatable(true)}
                        onUnmount={() => setIsRenderingCreatable(false)}
                        option={row}
                        portalRoot={creatablePortalRef.current}
                        shouldRenderDivider={virtualOptions.length > 1}
                        verticalItemAlignment={verticalItemAlignment}
                      />
                    );
                  }

                  /**
                   * These ungrouped items occur after any group with a divider
                   * We need to shift them down so they aren't too close to the last border.
                   */
                  const isAfterGroupsWithDividers =
                    hasGroupDividers &&
                    typeof groupIndices[0] === 'number' &&
                    option.index > groupIndices[0];

                  const transform = isAfterGroupsWithDividers
                    ? `translateY(calc(${option.start}px + ${tokens.ui.rowItem.padding.comfortable.vertical} + ${tokens.space[1]})`
                    : `translateY(${option.start}px)`;

                  return (
                    <div
                      key={option.index}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform,
                      }}
                    >
                      <SearchableListItemWrapper
                        density={density}
                        measureElement={virtualizer.measureElement}
                        option={row}
                        renderOption={renderOption}
                        verticalItemAlignment={verticalItemAlignment}
                        virtualIndex={option.index}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <AutocompleteEmptyState
              renderEmptyStateLabel={renderEmptyStateLabel}
            />
          )}
        </AutocompleteListBoxContentView>
        {isCreatable ? <div ref={creatablePortalRef} /> : null}
        {autocompleteNote && virtualOptions.length > 0 ? (
          <AutocompleteNote ref={noteRef} setNoteHeight={setNoteHeight}>
            {autocompleteNote}
          </AutocompleteNote>
        ) : null}
      </AutocompleteListBoxView>
    );
  }
);
